import { useEffect, useState } from 'react';

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';

import { db } from '../../core/firebase/firebase';


function timestampToMilliseconds(value) {
  if (!value) return 0;

  try {
    if (typeof value?.toMillis === 'function') {
      return value.toMillis();
    }

    if (typeof value?.toDate === 'function') {
      return value.toDate().getTime();
    }

    return new Date(value).getTime();
  } catch {
    return 0;
  }
}


function formatAnnouncementDate(value) {
  if (!value) return '—';

  try {
    const date =
      typeof value?.toDate === 'function'
        ? value.toDate()
        : new Date(value);

    return new Intl.DateTimeFormat('fr-TN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  } catch {
    return '—';
  }
}


export default function AnnouncementsAdminPage() {
  /* =========================================================
     NOUVELLE ANNONCE
     ========================================================= */

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');

  const [publishing, setPublishing] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');


  /* =========================================================
     HISTORIQUE
     ========================================================= */

  const [announcements, setAnnouncements] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] =
    useState(true);


  /* =========================================================
     VOIR UNE ANNONCE
     ========================================================= */

  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState(null);


  /* =========================================================
     MODIFIER UNE ANNONCE
     ========================================================= */

  const [editingAnnouncement, setEditingAnnouncement] =
    useState(null);

  const [editTitle, setEditTitle] = useState('');
  const [editMessage, setEditMessage] = useState('');

  const [savingEdit, setSavingEdit] = useState(false);


  /* =========================================================
     CHARGEMENT TEMPS RÉEL DES ANNONCES
     ========================================================= */

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'announcements'),

      (snapshot) => {
        const rows = snapshot.docs
          .map((announcementDoc) => ({
            id: announcementDoc.id,
            ...announcementDoc.data()
          }))

          // Cette page est réservée aux annonces générales.
          .filter((announcement) => {
            return (
              announcement.audienceType === 'general' ||
              announcement.audienceType === 'all'
            );
          })

          // Les plus récentes en premier.
          .sort((a, b) => {
            const dateA = timestampToMilliseconds(
              a.createdAt || a.publishedAt
            );

            const dateB = timestampToMilliseconds(
              b.createdAt || b.publishedAt
            );

            return dateB - dateA;
          });

        setAnnouncements(rows);
        setLoadingAnnouncements(false);
      },

      (err) => {
        console.error('Chargement annonces générales :', err);

        setLoadingAnnouncements(false);

        setErrorMessage(
          'Impossible de charger les annonces générales.'
        );
      }
    );

    return () => unsubscribe();
  }, []);


  /* =========================================================
     PUBLIER UNE NOUVELLE ANNONCE
     ========================================================= */

  async function publishAnnouncement() {
    const cleanTitle = title.trim();
    const cleanMessage = message.trim();

    if (!cleanTitle || !cleanMessage) {
      setErrorMessage(
        'Veuillez renseigner le titre et le message.'
      );

      setSuccessMessage('');
      return;
    }

    setPublishing(true);

    setErrorMessage('');
    setSuccessMessage('');

    try {
      await addDoc(
        collection(db, 'announcements'),
        {
          title: cleanTitle,
          message: cleanMessage,

          audienceType: 'general',
          audienceLabel: 'Tous les parents',

          published: true,

          createdAt: serverTimestamp(),
          publishedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      );

      setTitle('');
      setMessage('');

      setSuccessMessage(
        "L'annonce générale a été publiée avec succès."
      );
    } catch (err) {
      console.error(
        'Publication annonce générale :',
        err
      );

      setErrorMessage(
        "Impossible de publier l'annonce."
      );
    } finally {
      setPublishing(false);
    }
  }


  /* =========================================================
     VOIR UNE ANNONCE
     ========================================================= */

  function openAnnouncement(announcement) {
    setSelectedAnnouncement(announcement);

    setEditingAnnouncement(null);

    setErrorMessage('');
    setSuccessMessage('');
  }


  /* =========================================================
     COMMENCER LA MODIFICATION
     ========================================================= */

  function startEditingAnnouncement(announcement) {
    setEditingAnnouncement(announcement);

    setEditTitle(announcement.title || '');
    setEditMessage(announcement.message || '');

    setSelectedAnnouncement(null);

    setErrorMessage('');
    setSuccessMessage('');
  }


  /* =========================================================
     ANNULER LA MODIFICATION
     ========================================================= */

  function cancelEditingAnnouncement() {
    setEditingAnnouncement(null);

    setEditTitle('');
    setEditMessage('');
  }


  /* =========================================================
     ENREGISTRER LA MODIFICATION
     ========================================================= */

  async function saveAnnouncementEdit() {
    if (!editingAnnouncement) return;

    const cleanTitle = editTitle.trim();
    const cleanMessage = editMessage.trim();

    if (!cleanTitle || !cleanMessage) {
      setErrorMessage(
        'Veuillez renseigner le titre et le message.'
      );

      return;
    }

    setSavingEdit(true);

    setErrorMessage('');
    setSuccessMessage('');

    try {
      await updateDoc(
        doc(
          db,
          'announcements',
          editingAnnouncement.id
        ),
        {
          title: cleanTitle,
          message: cleanMessage,

          updatedAt: serverTimestamp()
        }
      );

      setSuccessMessage(
        "L'annonce a été modifiée avec succès."
      );

      cancelEditingAnnouncement();
    } catch (err) {
      console.error(
        'Modification annonce :',
        err
      );

      setErrorMessage(
        "Impossible de modifier l'annonce."
      );
    } finally {
      setSavingEdit(false);
    }
  }


  /* =========================================================
     PUBLIER / DÉPUBLIER
     ========================================================= */

  async function toggleAnnouncementPublished(
    announcement
  ) {
    const nextPublished =
      announcement.published !== true;

    setErrorMessage('');
    setSuccessMessage('');

    try {
      const updateData = {
        published: nextPublished,
        updatedAt: serverTimestamp()
      };

      if (nextPublished) {
        updateData.publishedAt = serverTimestamp();
      }

      await updateDoc(
        doc(
          db,
          'announcements',
          announcement.id
        ),
        updateData
      );

      setSuccessMessage(
        nextPublished
          ? "L'annonce a été publiée."
          : "L'annonce a été dépubliée."
      );

      if (
        selectedAnnouncement?.id === announcement.id
      ) {
        setSelectedAnnouncement((current) => ({
          ...current,
          published: nextPublished
        }));
      }
    } catch (err) {
      console.error(
        'Publication / dépublication annonce :',
        err
      );

      setErrorMessage(
        "Impossible de modifier le statut de l'annonce."
      );
    }
  }


  /* =========================================================
     SUPPRIMER
     ========================================================= */

  async function removeAnnouncement(announcement) {
    const confirmed = window.confirm(
      `Supprimer définitivement l'annonce « ${
        announcement.title || 'Sans titre'
      } » ?`
    );

    if (!confirmed) return;

    setErrorMessage('');
    setSuccessMessage('');

    try {
      await deleteDoc(
        doc(
          db,
          'announcements',
          announcement.id
        )
      );

      if (
        selectedAnnouncement?.id === announcement.id
      ) {
        setSelectedAnnouncement(null);
      }

      if (
        editingAnnouncement?.id === announcement.id
      ) {
        cancelEditingAnnouncement();
      }

      setSuccessMessage(
        "L'annonce a été supprimée."
      );
    } catch (err) {
      console.error(
        'Suppression annonce :',
        err
      );

      setErrorMessage(
        "Impossible de supprimer l'annonce."
      );
    }
  }


  /* =========================================================
     INTERFACE
     ========================================================= */

  return (
    <section className="announcements-admin-page">

      {/* =====================================================
          EN-TÊTE
          ===================================================== */}

      <div className="announcements-admin-header">
        <div>
          <p className="eyebrow">
            COMMUNICATION
          </p>

          <h1>
            Annonces générales
          </h1>

          <p>
            Publiez une annonce qui sera visible
            par tous les parents, quel que soit
            le niveau de leur enfant.
          </p>
        </div>
      </div>


      {/* =====================================================
          MESSAGES
          ===================================================== */}

      {errorMessage && (
        <div className="announcements-message error">
          {errorMessage}
        </div>
      )}

      {successMessage && (
        <div className="announcements-message success">
          {successMessage}
        </div>
      )}


      {/* =====================================================
          CRÉATION
          ===================================================== */}

      <div className="panel announcements-admin-form">

        <div>
          <p className="eyebrow">
            NOUVELLE ANNONCE
          </p>

          <h2>
            Créer une annonce
          </h2>
        </div>


        <div className="announcements-field">
          <label htmlFor="announcement-title">
            Titre de l'annonce
          </label>

          <input
            id="announcement-title"
            type="text"
            value={title}
            onChange={(event) =>
              setTitle(event.target.value)
            }
            placeholder="Ex. Réunion des parents"
            maxLength={120}
          />
        </div>


        <div className="announcements-field">
          <label htmlFor="announcement-message">
            Message
          </label>

          <textarea
            id="announcement-message"
            value={message}
            onChange={(event) =>
              setMessage(event.target.value)
            }
            placeholder="Écrivez votre annonce..."
            rows={7}
          />
        </div>


        <div className="announcements-audience">
          <div>
            <strong>
              Destinataires
            </strong>

            <span>
              Tous les parents — Tous les niveaux
            </span>
          </div>

          <span className="announcements-audience-badge">
            Annonce générale
          </span>
        </div>


        <div className="announcements-actions">
          <button
            type="button"
            className="button button-primary"
            onClick={publishAnnouncement}
            disabled={publishing}
          >
            {publishing
              ? 'Publication en cours…'
              : "Publier l'annonce"}
          </button>
        </div>

      </div>


      {/* =====================================================
          HISTORIQUE
          ===================================================== */}

      <section className="panel announcements-history">

        <div className="announcements-history-head">
          <div>
            <p className="eyebrow">
              HISTORIQUE
            </p>

            <h2>
              Annonces générales
            </h2>

            <p>
              Consultez, modifiez ou retirez
              les annonces destinées aux parents.
            </p>
          </div>

          <span className="announcements-count">
            {announcements.length}{' '}
            annonce
            {announcements.length > 1 ? 's' : ''}
          </span>
        </div>


        {loadingAnnouncements && (
          <div className="parent-empty">
            Chargement des annonces…
          </div>
        )}


        {!loadingAnnouncements &&
          announcements.length === 0 && (
            <div className="parent-empty">
              Aucune annonce pour le moment.
            </div>
          )}


        {!loadingAnnouncements &&
          announcements.length > 0 && (
            <div className="announcements-list">

              {announcements.map(
                (announcement) => (

                  <article
                    key={announcement.id}
                    className="announcement-admin-card"
                  >

                    <div className="announcement-admin-main">

                      <div className="announcement-admin-title-row">

                        <h3>
                          {announcement.title ||
                            'Sans titre'}
                        </h3>

                        <span
                          className={
                            announcement.published === true
                              ? 'announcement-status published'
                              : 'announcement-status unpublished'
                          }
                        >
                          {announcement.published === true
                            ? 'Publiée'
                            : 'Dépubliée'}
                        </span>

                      </div>


                      <p className="announcement-admin-message">
                        {announcement.message ||
                          'Aucun message.'}
                      </p>


                      <div className="announcement-admin-meta">

                        <span>
                          Tous les parents
                        </span>

                        <span>
                          {formatAnnouncementDate(
                            announcement.publishedAt ||
                              announcement.createdAt
                          )}
                        </span>

                      </div>

                    </div>


                    <div className="announcement-admin-actions">

                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={() =>
                          openAnnouncement(
                            announcement
                          )
                        }
                      >
                        Voir
                      </button>


                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={() =>
                          startEditingAnnouncement(
                            announcement
                          )
                        }
                      >
                        Modifier
                      </button>


                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={() =>
                          toggleAnnouncementPublished(
                            announcement
                          )
                        }
                      >
                        {announcement.published === true
                          ? 'Dépublier'
                          : 'Publier'}
                      </button>


                      <button
                        type="button"
                        className="button announcement-delete-button"
                        onClick={() =>
                          removeAnnouncement(
                            announcement
                          )
                        }
                      >
                        Supprimer
                      </button>

                    </div>

                  </article>

                )
              )}

            </div>
          )}

      </section>


      {/* =====================================================
          MODALE VOIR
          ===================================================== */}

      {selectedAnnouncement && (
        <div className="announcement-modal-backdrop">

          <div className="announcement-modal">

            <div className="announcement-modal-head">

              <div>
                <p className="eyebrow">
                  ANNONCE
                </p>

                <h2>
                  {selectedAnnouncement.title ||
                    'Sans titre'}
                </h2>
              </div>


              <button
                type="button"
                className="announcement-close-button"
                onClick={() =>
                  setSelectedAnnouncement(null)
                }
                aria-label="Fermer"
              >
                ×
              </button>

            </div>


            <div className="announcement-modal-status">

              <span
                className={
                  selectedAnnouncement.published === true
                    ? 'announcement-status published'
                    : 'announcement-status unpublished'
                }
              >
                {selectedAnnouncement.published === true
                  ? 'Publiée'
                  : 'Dépubliée'}
              </span>


              <span>
                Tous les parents — Tous les niveaux
              </span>

            </div>


            <div className="announcement-modal-message">
              {selectedAnnouncement.message ||
                'Aucun message.'}
            </div>


            <div className="announcement-modal-date">
              Publication :{' '}
              {formatAnnouncementDate(
                selectedAnnouncement.publishedAt ||
                  selectedAnnouncement.createdAt
              )}
            </div>


            <div className="announcement-modal-footer">

              <button
                type="button"
                className="button button-secondary"
                onClick={() =>
                  setSelectedAnnouncement(null)
                }
              >
                Fermer
              </button>


              <button
                type="button"
                className="button button-primary"
                onClick={() =>
                  startEditingAnnouncement(
                    selectedAnnouncement
                  )
                }
              >
                Modifier
              </button>

            </div>

          </div>

        </div>
      )}


      {/* =====================================================
          MODALE MODIFIER
          ===================================================== */}

      {editingAnnouncement && (
        <div className="announcement-modal-backdrop">

          <div className="announcement-modal">

            <div className="announcement-modal-head">

              <div>
                <p className="eyebrow">
                  MODIFICATION
                </p>

                <h2>
                  Modifier l'annonce
                </h2>
              </div>


              <button
                type="button"
                className="announcement-close-button"
                onClick={
                  cancelEditingAnnouncement
                }
                aria-label="Fermer"
              >
                ×
              </button>

            </div>


            <div className="announcements-field">

              <label htmlFor="edit-announcement-title">
                Titre
              </label>

              <input
                id="edit-announcement-title"
                type="text"
                value={editTitle}
                onChange={(event) =>
                  setEditTitle(
                    event.target.value
                  )
                }
                maxLength={120}
              />

            </div>


            <div className="announcements-field">

              <label htmlFor="edit-announcement-message">
                Message
              </label>

              <textarea
                id="edit-announcement-message"
                value={editMessage}
                onChange={(event) =>
                  setEditMessage(
                    event.target.value
                  )
                }
                rows={8}
              />

            </div>


            <div className="announcement-modal-footer">

              <button
                type="button"
                className="button button-secondary"
                onClick={
                  cancelEditingAnnouncement
                }
              >
                Annuler
              </button>


              <button
                type="button"
                className="button button-primary"
                onClick={
                  saveAnnouncementEdit
                }
                disabled={savingEdit}
              >
                {savingEdit
                  ? 'Enregistrement…'
                  : 'Enregistrer les modifications'}
              </button>

            </div>

          </div>

        </div>
      )}

    </section>
  );
}